(() => {
  if (customElements.get('gvs-sticky-collection-nav')) return;

  class GVSStickyCollectionNav extends HTMLElement {
    connectedCallback() {
      this.links = Array.from(this.querySelectorAll('[data-gvs-nav-target]'));
      this.list = this.querySelector('.gvs-sticky-collection-nav__list');
      this.stickyWrapper = this.closest('[id^="shopify-section-"]');
      this.enableActiveTracking = this.dataset.activeTracking === 'true';
      this.enableSmoothScroll = this.dataset.smoothScroll === 'true';
      this.stickyEnabled = this.dataset.stickyEnabled === 'true';
      this.mobileOffset = Number(this.dataset.mobileOffset) || 0;
      this.desktopOffset = Number(this.dataset.desktopOffset) || 0;
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.railMedia = window.matchMedia('(max-width: 1299px)');
      this.ticking = false;
      this.currentActiveLink = null;

      this.onClick = this.onClick.bind(this);
      this.onViewportUpdate = this.onViewportUpdate.bind(this);

      this.links.forEach((link) => link.addEventListener('click', this.onClick));
      window.addEventListener('scroll', this.onViewportUpdate, { passive: true });
      window.addEventListener('resize', this.onViewportUpdate, { passive: true });
      window.addEventListener('pageshow', this.onViewportUpdate, { passive: true });

      this.header = document.querySelector('#header-component');
      this.headerHeight = this.readHeaderHeight();
      this.resizeObserver = new ResizeObserver(() => {
        this.headerHeight = this.readHeaderHeight();
        this.onViewportUpdate();
      });
      this.resizeObserver.observe(this);
      if (this.stickyWrapper) this.resizeObserver.observe(this.stickyWrapper);
      if (this.header) this.resizeObserver.observe(this.header);

      this.onViewportUpdate();
    }

    disconnectedCallback() {
      this.links?.forEach((link) => link.removeEventListener('click', this.onClick));
      window.removeEventListener('scroll', this.onViewportUpdate);
      window.removeEventListener('resize', this.onViewportUpdate);
      window.removeEventListener('pageshow', this.onViewportUpdate);
      this.resizeObserver?.disconnect();

      if (document.documentElement.dataset.gvsStickyNavOwner === this.id) {
        this.clearMobileHeight();
      }
    }

    readHeaderHeight() {
      const cssValue = Number.parseFloat(
        getComputedStyle(document.body).getPropertyValue('--header-height')
      );

      if (Number.isFinite(cssValue) && cssValue > 0) return cssValue;
      return this.header?.getBoundingClientRect().height || 0;
    }

    getHeaderHeight() {
      return this.headerHeight || 0;
    }

    getConfiguredOffset() {
      return this.mobileMedia.matches ? this.mobileOffset : this.desktopOffset;
    }

    getStickyTop() {
      return Math.max(this.getHeaderHeight(), this.getConfiguredOffset());
    }

    getScrollOffset() {
      const navHeight = this.getBoundingClientRect().height;
      return this.getStickyTop() + navHeight + 12;
    }

    getTarget(link) {
      const targetId = link.dataset.gvsNavTarget;
      if (!targetId) return null;
      return document.getElementById(targetId);
    }

    onClick(event) {
      const link = event.currentTarget;
      const target = this.getTarget(link);

      if (!target) return;

      event.preventDefault();

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const top = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();

      window.scrollTo({
        top: Math.max(0, top),
        behavior: this.enableSmoothScroll && !prefersReducedMotion ? 'smooth' : 'auto',
      });

      if (window.history?.replaceState) {
        window.history.replaceState(null, '', `#${target.id}`);
      }

      this.setActiveLink(link, true);
    }

    onViewportUpdate() {
      if (this.ticking) return;

      this.ticking = true;
      requestAnimationFrame(() => {
        const stickyTop = this.getStickyTop();
        const stickyElement = this.stickyWrapper || this;
        const stickyRect = stickyElement.getBoundingClientRect();
        const navRect = this.getBoundingClientRect();
        const offset = stickyTop + navRect.height + 12;
        const targetMetrics = this.enableActiveTracking
          ? this.links
              .map((link) => {
                const target = this.getTarget(link);
                return target ? { link, rect: target.getBoundingClientRect() } : null;
              })
              .filter(Boolean)
          : [];

        this.updateStickyState({ stickyTop, stickyRect, navRect });

        if (this.enableActiveTracking && targetMetrics.length) {
          this.updateActiveLink(offset, targetMetrics);
        }

        this.ticking = false;
      });
    }

    updateStickyState({ stickyTop, stickyRect, navRect }) {
      if (!this.stickyEnabled) {
        this.classList.remove('is-stuck');
        this.clearMobileHeight();
        return;
      }
      const isStuck =
        stickyRect.top <= stickyTop + 2 &&
        stickyRect.bottom > stickyTop + 2 &&
        window.scrollY > 0;

      this.classList.toggle('is-stuck', isStuck);

      if (this.mobileMedia.matches && isStuck) {
        document.documentElement.dataset.gvsStickyNavOwner = this.id;
        document.documentElement.style.setProperty(
          '--gvs-mobile-sticky-nav-height',
          `${Math.ceil(navRect.height)}px`
        );
      } else if (document.documentElement.dataset.gvsStickyNavOwner === this.id) {
        this.clearMobileHeight();
      }
    }

    clearMobileHeight() {
      document.documentElement.style.setProperty('--gvs-mobile-sticky-nav-height', '0px');
      delete document.documentElement.dataset.gvsStickyNavOwner;
    }

    // updateActiveLink(offset, targetMetrics) {
    //   let activeLink = null;
    //   let latestPassed = null;

    //   for (const item of targetMetrics) {
    //     if (item.rect.top <= offset && item.rect.bottom > offset) {
    //       activeLink = item.link;
    //       break;
    //     }

    //     if (item.rect.top <= offset && (!latestPassed || item.rect.top > latestPassed.rect.top)) {
    //       latestPassed = item;
    //     }
    //   }

    //   if (!activeLink) activeLink = latestPassed?.link ?? this.links[0];
    //   this.setActiveLink(activeLink);
    // }

    // START
    updateActiveLink(offset, targetMetrics) {
      let active = null;

      for (const item of targetMetrics) {
        const { top, bottom } = item.rect;
        if (top > offset) continue; // not reached yet

        if (
          !active ||
          top > active.rect.top ||
          // same top (parent and child start together): pick the smaller, inner one
          (top === active.rect.top && bottom < active.rect.bottom)
        ) {
          active = item;
        }
      }

      this.setActiveLink(active?.link ?? this.links[0]);
    }
    // END

    setActiveLink(activeLink, forceScroll = false) {
      if (!activeLink) return;

      const changed = activeLink !== this.currentActiveLink;
      this.currentActiveLink = activeLink;

      this.links.forEach((link) => {
        const isActive = link === activeLink;
        link.classList.toggle('is-active', isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (changed || forceScroll) this.keepActiveLinkInView(activeLink);
    }

    keepActiveLinkInView(link) {
      if (!this.list || !this.railMedia.matches) return;

      const listRect = this.list.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const isOutOfView = linkRect.left < listRect.left || linkRect.right > listRect.right;

      if (!isOutOfView) return;

      const left =
        this.list.scrollLeft +
        linkRect.left -
        listRect.left -
        (listRect.width - linkRect.width) / 2;

      this.list.scrollTo({
        left: Math.max(0, left),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }
  }

  customElements.define('gvs-sticky-collection-nav', GVSStickyCollectionNav);
})();